import { Combobox } from '@/components/ui/combobox/combobox';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { useDealContext } from '@/contexts/deal-provider';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import { HOTKEYS } from '@/lib/hotkeys';
import type { Deal } from '@/models/registry';
import { noop } from 'lodash';
import { useMemo } from 'react';
import { getDeliveryQueueOptionsMap } from './delivery-queue-selector';
import getDeliveryQueueHealthClasses from './get-delivery-queue-health-classes';
import useDeliveryQueueMutations from './hooks/use-delivery-queue-mutations';

export function DeliveryQueueCombobox({
  pickupDate,
  delivery_date,
  date_added,
  dealId,
  afterOnChange,
  onSuccess,
  onError,
}: {
  pickupDate: Deal['pickup_date'];
  delivery_date: Deal['delivery_date'];
  date_added: Deal['date_added_to_customer_delivery_queue'];
  dealId: Deal['id'];
  afterOnChange?: () => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { enqueueDealMutation, dequeueDealMutation } =
    useDeliveryQueueMutations({ dealId, onSuccess, onError });

  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);

  const handleDeliveryQueueChange = () => {
    // if there is a date_added already, remove it, otherwise create a new date string and pass it to the mutation
    if (date_added) {
      dequeueDealMutation.mutate();
    } else {
      enqueueDealMutation.mutate({
        date_added: new Date().toISOString(),
      });
    }

    afterOnChange?.();
  };

  const deliveryQueueHealthClasses = useMemo(
    () =>
      getDeliveryQueueHealthClasses({
        pickupDateOrDeliveryDate: pickupDate || delivery_date,
        deal: deal!,
      }),
    [pickupDate, delivery_date, deal],
  );

  const DELIVERY_QUEUE_OPTIONS_MAP = useMemo(
    () => getDeliveryQueueOptionsMap(deliveryQueueHealthClasses),
    [deliveryQueueHealthClasses],
  );

  return (
    <Combobox
      endAdornment={
        <KeyboardKeyIcon>{HOTKEYS.DEAL_DELIVERY_QUEUE}</KeyboardKeyIcon>
      }
      placeholder="Set delivery queued..."
      emptyMessage="No options found"
      options={DELIVERY_QUEUE_OPTIONS_MAP}
      onKeyDown={(e) => {
        const keyPressed = e.key;

        const option = DELIVERY_QUEUE_OPTIONS_MAP.find(
          (option) => option.hotkey === keyPressed,
        );

        if (option) {
          handleDeliveryQueueChange();
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onChange={handleDeliveryQueueChange}
      setOpen={noop}
    ></Combobox>
  );
}
